import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * A PHOTOGRAPH WITH A PRODUCT OBJECT BREAKING ITS FRAME.
 *
 * Neither half works alone. A photograph of somebody at a laptop is stock; a
 * UI card on a cream ground is cold and reads as a diagram. Together — with
 * the card pulled OFF the photo's edge rather than sitting politely beside it
 * — they read as one designed object: the warmth comes from the picture, the
 * proof comes from the card, and the overlap is what stops it looking like a
 * screenshot pasted next to a person.
 *
 * Two rules the composition depends on:
 *
 *   1. THE CARD MUST OVERLAP. Placed inside the photo it becomes a caption;
 *      placed clear of it, two unrelated objects. Roughly a third across the
 *      edge is where it reads as deliberate.
 *   2. THE PHOTO IS NEVER FULL WIDTH OF ITS COLUMN. It needs the card's
 *      overhang to sit somewhere, and the asymmetry is what gives the pair a
 *      direction.
 *
 * Below `md` the card stops being absolute and stacks under the photo with a
 * negative margin — still overlapping, but never overflowing a phone.
 */
export function PhotoPlate({
  src,
  card,
  /** Which side the card breaks out on. The photo takes the other side. */
  side = 'left',
  /**
   * 'break' — the card hangs off the photo's edge (the default composition).
   * 'inset' — the card lies ON the photograph as a frosted panel across its
   *   middle, and the photo fills its column square. Use this when the card
   *   is something happening TO the person in the picture — an approval
   *   prompt is addressed to them, so it belongs inside their frame rather
   *   than beside it.
   */
  variant = 'break',
  className = ''
}: {
  src: string;
  card: React.ReactNode;
  side?: 'left' | 'right';
  variant?: 'break' | 'inset';
  className?: string;
}) {
  const reduce = useReducedMotion();
  if (variant === 'inset') {
    return <motion.div initial={reduce ? false : {
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      amount: 0.3
    }} transition={{
      duration: 0.7,
      ease: [0.23, 1, 0.32, 1]
    }} className={`relative overflow-hidden rounded-[22px] bg-[#F1EAE0] ${className}`} data-id="element-269" data-mp="27tbfgjrelwm9">
        <div className="aspect-[4/5] w-full sm:aspect-square" data-id="element-270" data-mp="1zfgekniop8rk">
          <img src={src} alt="" aria-hidden="true" loading="lazy" className="size-full object-cover" data-id="element-271" data-mp="24am0hmc36nfn" />
        </div>
        {/* Across the middle, edge to edge but for a small inset — the panel
         reads as laid over the scene, not as a second picture inside it. */}
        <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 md:inset-x-4" data-id="element-272" data-mp="1gndxjb24rqgs">{card}</div>
      </motion.div>;
  }
  const photoSide = side === 'left' ? 'md:ml-auto' : 'md:mr-auto';
  const cardSide = side === 'left' ? 'md:left-0' : 'md:right-0';
  return <motion.div initial={reduce ? false : {
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.3
  }} transition={{
    duration: 0.7,
    ease: [0.23, 1, 0.32, 1]
  }} className={`relative ${className}`} data-id="element-273" data-mp="17o0ub04lny4i">
      {/* The plate. Warm-toned and softly rounded to the site's 22px so it
       belongs to the same family as every card around it. */}
      <div className={`overflow-hidden rounded-[22px] bg-[#F1EAE0] ${photoSide} aspect-[4/3] w-full md:aspect-[4/3] md:w-[74%]`} data-id="element-274" data-mp="x0st97rcxhts">
        <img src={src} alt="" aria-hidden="true" loading="lazy" className="size-full object-cover" data-id="element-275" data-mp="1bxa31ujweoe8" />
      </div>

      {/* The object. Absolute and vertically centred from md up; stacked and
       pulled upward below it. */}
      <div className={`relative z-10 mx-auto -mt-16 flex w-[94%] justify-center md:absolute ${cardSide} md:top-1/2 md:mx-0 md:mt-0 md:w-[58%] md:-translate-y-1/2 md:justify-start`} data-id="element-276" data-mp="1kpvl18fpgr20">
        {card}
      </div>
    </motion.div>;
}